import { UiUtils } from 'ui-components'
import { AppServices, AssetType, OrderType, TrustType, type OrderSellReq } from 'network'
import { useAssetsStore, useBaseInfoStore, useKlineStore, useSpotOrderListStore } from 'store'
import { useI18n } from 'i18n-utils'

export enum ButtonType {
  Market = 'Market',
  Limit = 'Limit',
  StopLoss = 'StopLoss',
}

export const useSpotOrdererSell = () => {
  /** 交易额 */
  const tradeAmount = ref('')
  const klineStore = useKlineStore()
  const assetsStore = useAssetsStore()
  const buttonType = ref<ButtonType>(ButtonType.Market)
  const slider = ref(0)
  const baseInfoStore = useBaseInfoStore()
  const { t } = useI18n()

  /** 卖出数量 */
  const sellAmount = ref('')

  /** 卖出价格 */
  const sellPrice = ref('')

  /** 是否初始化设置过金额 */
  const isInitPrice = ref(false)

  /** 卖出数量是否聚焦 */
  const isSellAmountFocus = ref(false)

  /** 是否正在卖出 */
  const isLoading = ref(false)

  const spotOrderListStore = useSpotOrderListStore()

  const fromSpotSymbolName = computed(() => {
    return klineStore.getSpotFromSymbolName()
  })

  /** 可用资产 */
  const availableAssets = computed(() => {
    if (!klineStore.currentSymbolId) {
      return ''
    }
    const staticData = baseInfoStore.getCoinStaticItemDataBySymbolId(klineStore.currentSymbolId)
    if (!staticData) return assetsStore.availableAssets
    const coinFrom = staticData.coinFrom
    const data = assetsStore.assets?.remain.list.find((item) => item.itemId === coinFrom)
    return data?.over || ''
  })

  const getMarks = (value: number) => {
    if (value === slider.value) {
      return `${value}%`
    }
    return ''
  }

  const sliderMarks = computed(() => {
    // 只有当slider的值等对应的百分比时显示marks，其他都显示空
    const marks = {
      0: getMarks(0),
      25: getMarks(25),
      50: getMarks(50),
      75: getMarks(75),
      100: getMarks(100),
    }
    return marks
  })
  const buttonTypeList = [
    {
      label: '市价',
      value: ButtonType.Market,
    },
    {
      label: '限价',
      value: ButtonType.Limit,
    },
  ]

  watchEffect(() => {
    if (!isInitPrice.value && klineStore.currentSpotKline) {
      isInitPrice.value = true
      sellPrice.value = `${klineStore.currentSpotKline?.close || ''}`
    }
  })

  watchEffect(() => {
    if (buttonType.value === ButtonType.Market) {
      sellPrice.value = `${klineStore.currentSpotKline?.close?.decimalPadZero(klineStore.coinToPrecision) || ''}`
    }
  })

  watchEffect(() => {
    const price = buttonType.value === ButtonType.Market ? klineStore.currentSpotKline?.close : sellPrice.value
    if (!price) return
    tradeAmount.value = price.multiplyStr(sellAmount.value).cutDecimal(klineStore.coinToPrecision)
  })

  const orderSell = async (successCb?: () => void) => {
    if (isLoading.value) return
    if (!klineStore.currentSymbolId) {
      return
    }

    if (buttonType.value === ButtonType.Limit && !sellPrice.value) {
      UiUtils.showWarning(t('请输入卖出价格'))
      return
    }

    isLoading.value = true
    const req: OrderSellReq = {
      trustType: TrustType.Normal,
      assetType: AssetType.Spot,
      type: buttonType.value === ButtonType.Market ? OrderType.Market : OrderType.Limit,
      symbolId: klineStore.currentSymbolId,
      side: 'sell',
      num: sellAmount.value,
    }
    if (buttonType.value === ButtonType.Limit) {
      req.price = sellPrice.value
    }
    const res = await AppServices.spotOrderSell(req)
    if (res.status === 200) {
      UiUtils.showSuccess(t('下单成功'))
      assetsStore.refresh()
      spotOrderListStore.refresh()
      successCb?.()
    } else {
      UiUtils.showError(res.msg)
    }
    isLoading.value = false
  }

  const onClear = () => {
    sellAmount.value = ''
    slider.value = 0
    tradeAmount.value = '0.00'
  }

  const handleButtonTypeChange = (value: ButtonType) => {
    buttonType.value = value
    isInitPrice.value = true

    onClear()

    switch (value) {
      case ButtonType.Market:
        sellPrice.value = `${klineStore.currentSpotKline?.close || ''}`
        break
      case ButtonType.Limit:
        sellPrice.value = `${klineStore.currentSpotKline?.close || ''}`
        break
      case ButtonType.StopLoss:
        break
      default:
        break
    }
  }

  watchEffect(() => {
    if (isSellAmountFocus.value) return
    const value = slider.value
    const rate = value.divideStr(100)

    const price = availableAssets.value
    if (!price) return

    sellAmount.value = slider.value.isLessThanOrEqualTo(0) ? '' : availableAssets.value?.multiplyStr(rate).cutDecimal(klineStore.coinFromPrecision)
  })

  const onSliderInput = (value: number) => {
    if (isSellAmountFocus.value) return

    const rate = value.divideStr(100)

    const price = availableAssets.value
    if (!price) return
    sellAmount.value = availableAssets.value?.multiplyStr(rate).cutDecimal(klineStore.coinFromPrecision)
  }

  const onSellAmountFocus = () => {
    isSellAmountFocus.value = true
  }

  const onSellAmountBlur = () => {
    isSellAmountFocus.value = false
  }

  const onSellAmountInput = (evt: string | MouseEvent) => {
    let value = ''
    if (typeof evt === 'string') {
      value = evt
    } else {
      value = (evt.target as HTMLInputElement).value
    }
    if (!value) {
      slider.value = 0
      return
    }
    // 通过可用金额，计算出可最大购买的数量
    if (!availableAssets.value) return
    if (value.isGreaterThan(availableAssets.value)) {
      slider.value = 100
    } else {
      slider.value = value.divideStr(availableAssets.value).multiplyStr(100).cutDecimal(2).toNumber()
    }
  }

  return {
    onClear,
    sellPrice,
    sellAmount,
    buttonType,
    buttonTypeList,
    handleButtonTypeChange,
    slider,
    sliderMarks,
    fromSpotSymbolName,
    availableAssets,
    orderSell,
    tradeAmount,
    isSellAmountFocus,
    onSellAmountFocus,
    onSellAmountBlur,
    onSellAmountInput,
    isLoading,
    onSliderInput,
  }
}
